import { useMemo } from 'react'

const formatLastPublished = (dateInput) => {
  if (!dateInput) return null
  const date = new Date(dateInput)
  if (Number.isNaN(date.getTime())) return null

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  let hours = date.getHours()
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const ampm = hours >= 12 ? 'pm' : 'am'
  hours = hours % 12 || 12
  const hourStr = String(hours).padStart(2, '0')

  return `${day}/${month}/${year}, ${hourStr}:${minutes} ${ampm}`
}

export const useProjectPublishInfo = (projectData, isAiCompanionMode, publishedHref, connectedThirdPartyDomain = null) => {
  const active = projectData?.active || {}
  const projectSubdomain = projectData?.subdomain || ''
  const projectDomain = projectData?.domain || {}
  const wordpressPlugin = projectData?.integrations?.wordpressPlugin || {}
  const wordpressDomain = wordpressPlugin?.hostname || null
  const staticMedia = projectData?.staticMedia || []

  const domainSuffix = isAiCompanionMode ? 'cdsn.app' : 'codedesign.app'

  // Use third-party domain if available, otherwise fall back to project domain
  const effectiveDomain = connectedThirdPartyDomain || projectDomain

  // Domain verification status (check third-party domain first)
  const dnsVerified = connectedThirdPartyDomain
    ? !!connectedThirdPartyDomain.dnsVerified
    : !!projectDomain?.dnsVerified
  const txtVerified = connectedThirdPartyDomain
    ? !!connectedThirdPartyDomain.txtVerified
    : !!projectDomain?.txtVerified
  const isDomainVerified = dnsVerified && txtVerified
  // Support third-party (.domain), managed domains with NS (.domain_name), and other variations (.name)
  const domainName = connectedThirdPartyDomain?.domain || connectedThirdPartyDomain?.domain_name || connectedThirdPartyDomain?.name || projectDomain?.domain || null

  const isSubdomainActive = projectSubdomain && active?.subdomain === 'true'
  // Managed domains (with NS) use domain_name field and are always active. Third-party domains need verification.
  const isManagedDomain = !!connectedThirdPartyDomain?.domain_name
  const isDomainActive = domainName && (active?.domain === 'true' || isManagedDomain || (connectedThirdPartyDomain && isDomainVerified))
  const isWordpressActive = wordpressDomain && active?.wordpress === 'true'
  const isDomainConnected = !!domainName
  const isWordpressConnected = !!wordpressDomain
  const isPublished = isDomainActive || isSubdomainActive || isWordpressActive
  const canEditSubdomain = isSubdomainActive && !isDomainConnected && !isWordpressConnected
  const isFirstTimePublish = !isSubdomainActive && !isDomainConnected && !isWordpressConnected

  const displayUrl = useMemo(() => {
    if (isAiCompanionMode) {
      if (isDomainActive && domainName) return `https://${domainName}`
      if (isSubdomainActive && projectSubdomain) return `https://${projectSubdomain}.${domainSuffix}`
    } else {
      if (isWordpressActive && wordpressDomain) {
        return /^https?:\/\//i.test(wordpressDomain) ? wordpressDomain : `https://${wordpressDomain}`
      }
      if (isDomainActive && domainName) return `https://${domainName}`
      if (isSubdomainActive && projectSubdomain) return `https://${projectSubdomain}.${domainSuffix}`
    }
    return publishedHref || `https://your-site.${domainSuffix}`
  }, [isAiCompanionMode, isDomainActive, isWordpressActive, isSubdomainActive, domainName, projectSubdomain, wordpressDomain, domainSuffix, publishedHref])

  const lastPublished = useMemo(() => {
    if (isDomainActive && projectDomain?.updatedAt) return formatLastPublished(projectDomain.updatedAt)
    if (isSubdomainActive && projectData?.subdomainUpdatedAt) return formatLastPublished(projectData.subdomainUpdatedAt)
    if (isWordpressActive && wordpressPlugin?.lastUpdatedAt) return formatLastPublished(wordpressPlugin.lastUpdatedAt)
    return null
  }, [isDomainActive, isSubdomainActive, isWordpressActive, projectDomain?.updatedAt, projectData?.subdomainUpdatedAt, wordpressPlugin?.lastUpdatedAt])

  const assetsCount = useMemo(() => Array.isArray(staticMedia) ? staticMedia.length : 0, [staticMedia])

  return {
    projectSubdomain,
    projectDomain,
    wordpressPlugin,
    wordpressDomain,
    staticMedia,
    domainSuffix,
    isSubdomainActive,
    isDomainActive,
    isWordpressActive,
    isDomainConnected,
    isWordpressConnected,
    isPublished,
    canEditSubdomain,
    isFirstTimePublish,
    displayUrl,
    lastPublished,
    assetsCount,
    // New domain verification fields
    domainName,
    dnsVerified,
    txtVerified,
    isDomainVerified,
  }
}

